/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Pribyl (pribyl@cadwork.cz)
 *
 */

import WebGLRenderGraph, {ERenderFiler} from "App/render/webgl/render_graph";
import View from "App/view/view";
import Factory from "App/render/webgl/passes/factory";
import Pass from "App/render/webgl/passes/pass";

import AlphaTransparencyShader from "App/render/webgl/shaders/alpha_transparency";
import BillboardShader from "App/render/webgl/shaders/billboard";
import LineShader from "App/render/webgl/shaders/line";
import OpaqueShader from "App/render/webgl/shaders/opaque";
import OpaqueNoTextureShader from "App/render/webgl/shaders/opaque_no_texture";
import OutlineShader from "App/render/webgl/shaders/outline";
import ParticlesShader from "App/render/webgl/shaders/particles";
import PointShader from "App/render/webgl/shaders/point";
import PointCloudShader from "App/render/webgl/shaders/point_cloud";
import ShadowVolumeShader from "App/render/webgl/shaders/shadow_volume/shadow_volume";
import SkyBoxShader from "App/render/webgl/shaders/sky_box";
import SkyDomeShader from "App/render/webgl/shaders/sky_dome";

import {RenderOptionNodeTypeName} from "App/render/webgl/nodes/type_names";
import {
    AlphaTransparencyBinTypeName,
    BillboardBinTypeName,
    LinesBinTypeName,
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName,
    OutlinesBinTypeName,
    ParticlesBinTypeName,
    PoiBinTypeName,
    PointCloudsBinTypeName,
    PointsBinTypeName,
    ShadowVolumesBinTypeName,
    SkyBoxBinTypeName,
    SkyDomeBinTypeName,
    TextBinTypeName,
    TransparentBinTypeName, TransparentInternalBinTypeName,
    TransparentNoTextureBinTypeName,
    TransparentNoTextureInternalBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import {
    ScreenWithShadowVolumesPassTypeName,
    WeightedAverageTransparencyPassTypeName
} from "App/render/webgl/passes/type_names";
import IScene from "js_interface/src/scene_interface";


export default
class ScreenWithShadowVolumes extends Pass {

    public static TypeName: string = ScreenWithShadowVolumesPassTypeName;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this._requireLighting = true;
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ScreenWithShadowVolumes {
        return new ScreenWithShadowVolumes(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(AlphaTransparencyBinTypeName, shaders.get(AlphaTransparencyShader.TypeName));
        this.attachShaderToBin(BillboardBinTypeName, shaders.get(BillboardShader.TypeName));
        this.attachShaderToBin(LinesBinTypeName, shaders.get(LineShader.TypeName));
        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(OpaqueShader.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(OpaqueNoTextureShader.TypeName));
        this.attachShaderToBin(OutlinesBinTypeName, shaders.get(OutlineShader.TypeName));
        this.attachShaderToBin(ParticlesBinTypeName, shaders.get(ParticlesShader.TypeName));
        this.attachShaderToBin(PoiBinTypeName, shaders.get(PointShader.TypeName));
        this.attachShaderToBin(PointCloudsBinTypeName, shaders.get(PointCloudShader.TypeName));
        this.attachShaderToBin(PointsBinTypeName, shaders.get(PointShader.TypeName));
        this.attachShaderToBin(ShadowVolumesBinTypeName, shaders.get(ShadowVolumeShader.TypeName));
        this.attachShaderToBin(SkyBoxBinTypeName, shaders.get(SkyBoxShader.TypeName));
        this.attachShaderToBin(SkyDomeBinTypeName, shaders.get(SkyDomeShader.TypeName));
        this.attachShaderToBin(TextBinTypeName, shaders.get(OpaqueNoTextureShader.TypeName));
    }

    public _renderAmbient(aView: View) {
        let sceneAPI = aView.getDocument().getSceneAPI()
        let gl = this._context.getWebGLRenderingContext();

        let bins = this._renderGraph.getRenderBins();

        sceneAPI.lightingModule.pushCurrentLighting();

        sceneAPI.lightingModule.setMinimalDiffuseLight(0.0);

        if (!aView.isMoving()) {
            sceneAPI.lightingModule.setHeadLightIntensity(sceneAPI.lightingModule.getHeadLightIntensity() * (1.0 - sceneAPI.lightingModule.getShadowIntensity()));
            // do not cont diffuse color in case that the shadow intensity is maximal (black shadow)
            sceneAPI.lightingModule.setSunLightIntensity(sceneAPI.lightingModule.getSunLightIntensity() * (1.0 - sceneAPI.lightingModule.getShadowIntensity()));
        }

        gl.depthFunc(gl.LESS);
        gl.depthMask(true);

        // the render bins where the lighting/shadows will be applied
        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);
        bins.get(TextBinTypeName).render(this);
        bins.get(OutlinesBinTypeName).render(this);
        bins.get(LinesBinTypeName).render(this);
        bins.get(AlphaTransparencyBinTypeName).render(this);

        sceneAPI.lightingModule.popCurrentLighting();
    }

    public _blendDiffuseColor(aView) {
        let sceneAPI = aView.document.sceneAPI;
        let gl = this._context.getWebGLRenderingContext();

        sceneAPI.lightingModule.pushCurrentLighting();

        // here we just blend diffuse color for fragments which are not in shadow
        sceneAPI.lightingModule.setMinimalDiffuseLight(0.0); // do not use the minimal diffuse light for fragments which are not facing towards light

        // do not use the ambient light intensity as we blend only diffuse color
        sceneAPI.lightingModule.setAmbientLightIntensity(0.0);

        // blend the rest of the diffuse color from which is already written in the fragments from the ambient pass
        sceneAPI.lightingModule.setHeadLightIntensity(sceneAPI.lightingModule.getHeadLightIntensity() * sceneAPI.lightingModule.getShadowIntensity());
        sceneAPI.lightingModule.setSunLightIntensity(sceneAPI.lightingModule.getSunLightIntensity() * sceneAPI.lightingModule.getShadowIntensity());

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);

        gl.stencilFunc(gl.EQUAL, 0, 0xff);
        gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);

        let bins = this._renderGraph.getRenderBins();

        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);
        bins.get(AlphaTransparencyBinTypeName).render(this);
        bins.get(TextBinTypeName).render(this);
        bins.get(OutlinesBinTypeName).render(this);
        bins.get(LinesBinTypeName).render(this);

        gl.disable(gl.BLEND);

        sceneAPI.lightingModule.popCurrentLighting();
    }

    private _renderTransparentObjects(aView: View) {
        // test wheather there is something to render inside
        let bins = this._renderGraph.getRenderBins();
        if (!bins.get(TransparentInternalBinTypeName).hasChildren() &&
            !bins.get(TransparentNoTextureInternalBinTypeName).hasChildren() &&
            !bins.get(TransparentBinTypeName).hasChildren() &&
            !bins.get(TransparentNoTextureBinTypeName).hasChildren()) {
            return;
        }

        // render transparent objects to color and depth acc buffers
        this._renderGraph.getPasses().get(WeightedAverageTransparencyPassTypeName).render(
            aView,
            this._renderGraph.getFramebuffers().opaqueDepth,
            this._renderGraph.getFramebuffers().colorAccumulation,
            this._renderGraph.getFramebuffers().depthAccumulation,
            this._renderGraph.getFramebuffers().screen
        );
    }

    public render(aView) {
        let controls = aView.getControls();
        let camera = aView.getCamera();

        let oldLogDepthBufEnabled = controls.getUseLogarithmicDepthBuffer();
        controls.setUseLogarithmicDepthBuffer(false);

        let framebuffer = this._renderGraph.getFramebuffers().screen;

        let scale = framebuffer.parameters.scale;
        let gl = this._context.getWebGLRenderingContext();

        // set rendering target
        framebuffer.bind();

        let position = aView.getPosition();
        let size = aView.getSize();

        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);

        // as the preserveDrawingBuffer parameter of canvas is set to true, we need to clear the draw buffer here
        gl.clear(gl.STENCIL_BUFFER_BIT);

        let bins = this._renderGraph.getRenderBins();

        this._renderGraph.initGlStates();

        this.view = aView;

        // we have to render the skybox first otherwise is rewrites the objects with depth test off e.g. dynamic picking line
        bins.get(SkyBoxBinTypeName).render(this);
        bins.get(SkyDomeBinTypeName).render(this);

        // 1 .render geometry which can accept shadow with ambient color only
        this._renderAmbient(aView);

        // 2. render shadow bin (sides and caps) with writes to stencil buffer
        gl.enable(gl.STENCIL_TEST);
        if (!aView.isMoving()) {
            bins.get(ShadowVolumesBinTypeName).render(this);
        }

        // 3. blend diffuse color with stencil test
        this._blendDiffuseColor(aView);
        gl.disable(gl.STENCIL_TEST);

        // 4. objects not affected by shadows
        bins.get(PointCloudsBinTypeName).render(this);
        bins.get(PoiBinTypeName).render(this);
        bins.get(PointsBinTypeName).render(this);

        this._renderTransparentObjects(aView);

        // draw always on top geometry
        gl.disable(gl.DEPTH_TEST);

        // this is here because these two bins can contain the measuring line and picked primitive point marker
        this.state.setRenderNodesFilters([{
            type: RenderOptionNodeTypeName,
            value: ERenderFiler.alwaysOnTop
        }
        ]);

        bins.get(OutlinesBinTypeName).render(this);
        bins.get(PointsBinTypeName).render(this);

        this.state.resetRenderNodesFilters();

        gl.enable(gl.DEPTH_TEST);

        gl.disable(gl.SCISSOR_TEST);

        framebuffer.unbind(this._typeName);

        controls.setUseLogarithmicDepthBuffer(oldLogDepthBufEnabled);
    }
}

Factory.register(ScreenWithShadowVolumes);
